import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Building2, ArrowLeft, Shield, Lock, Server, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PrivacyPolicyPage = () => {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const response = await fetch(`${API_URL}/api/privacy-policy`);
        const data = await response.json();
        setPolicy(data);
      } catch (err) {
        console.error('Failed to fetch privacy policy:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPolicy();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="privacy-policy-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#1e3a5f] rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold text-[#1e3a5f]">FURD OpsFlow</span>
          </div>
          <Link to="/login">
            <Button variant="ghost" className="text-slate-600">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 bg-[#1e3a5f]/10 rounded-2xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-[#1e3a5f]" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-[#1e3a5f]">
                {policy?.title || 'Privacy Policy'}
              </h1>
              <p className="text-slate-500 text-sm mt-1">
                Last updated: {policy?.last_updated || 'February 2026'}
              </p>
            </div>
          </div>

          {/* Quick summary cards */}
          <div className="grid md:grid-cols-3 gap-4 mb-10">
            <div className="bg-slate-50 rounded-xl p-4">
              <Lock className="w-5 h-5 text-[#1e3a5f] mb-2" />
              <h3 className="font-semibold text-[#1e3a5f] text-sm">Data Security</h3>
              <p className="text-slate-500 text-xs mt-1">AES-256 encryption at rest, TLS 1.3 in transit</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <Server className="w-5 h-5 text-[#1e3a5f] mb-2" />
              <h3 className="font-semibold text-[#1e3a5f] text-sm">EU Hosted</h3>
              <p className="text-slate-500 text-xs mt-1">Data stored in London, UK with daily backups</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <Mail className="w-5 h-5 text-[#1e3a5f] mb-2" />
              <h3 className="font-semibold text-[#1e3a5f] text-sm">Your Rights</h3>
              <p className="text-slate-500 text-xs mt-1">Export, delete, or correct your data anytime</p>
            </div>
          </div>

          {/* Policy content */}
          <div className="prose prose-slate max-w-none">
            {policy?.content ? (
              <div 
                className="text-slate-600 leading-relaxed"
                dangerouslySetInnerHTML={{ 
                  __html: policy.content
                    .replace(/^# (.+)$/gm, '<h1 class="font-heading text-2xl font-bold text-[#1e3a5f] mt-8 mb-4">$1</h1>')
                    .replace(/^## (.+)$/gm, '<h2 class="font-heading text-xl font-semibold text-[#1e3a5f] mt-6 mb-3">$1</h2>')
                    .replace(/^- (.+)$/gm, '<li class="ml-4">$1</li>')
                    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                    .replace(/\n\n/g, '</p><p class="mb-4">')
                }}
              />
            ) : (
              <div className="text-slate-600 leading-relaxed space-y-6">
                <section>
                  <h2 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-3">1. Introduction</h2>
                  <p>FURD Tech Ltd (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) operates the OpsFlow platform. This policy explains how we collect, use, and protect your personal data in compliance with GDPR and UK data protection laws.</p>
                </section>

                <section>
                  <h2 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-3">2. Data We Collect</h2>
                  <ul className="list-disc ml-6 space-y-2">
                    <li><strong>Account information:</strong> Name, email address, role within your organization</li>
                    <li><strong>Activity data:</strong> Enquiries created, decisions made, quotes generated, outreach logs</li>
                    <li><strong>Technical data:</strong> IP address, login times, device information, browser type</li>
                  </ul>
                </section>

                <section>
                  <h2 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-3">3. How We Use Your Data</h2>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>To provide and improve our services</li>
                    <li>To communicate with you about your account</li>
                    <li>To ensure security and prevent fraud</li>
                    <li>To comply with legal obligations</li>
                  </ul>
                </section>

                <section>
                  <h2 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-3">4. Data Retention</h2>
                  <p>We retain your personal data for the period specified in your workspace settings (default: 24 months after last activity). You can request data export or deletion at any time through your Director.</p>
                </section>

                <section>
                  <h2 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-3">5. Your Rights Under GDPR</h2>
                  <ul className="list-disc ml-6 space-y-2">
                    <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
                    <li><strong>Right to Rectification:</strong> Correct inaccurate data</li>
                    <li><strong>Right to Erasure:</strong> Request deletion of your data</li>
                    <li><strong>Right to Portability:</strong> Export your data in a machine-readable format</li>
                    <li><strong>Right to Object:</strong> Object to certain processing of your data</li>
                    <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
                  </ul>
                </section>

                <section>
                  <h2 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-3">6. Data Security</h2>
                  <p>We implement industry-standard security measures:</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>AES-256 encryption for data at rest</li>
                    <li>TLS 1.3 encryption for data in transit</li>
                    <li>bcrypt password hashing with cost factor 12</li>
                    <li>Regular security audits and penetration testing</li>
                    <li>Access controls and comprehensive audit logging</li>
                  </ul>
                </section>

                <section>
                  <h2 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-3">7. Data Location</h2>
                  <p>Your data is hosted in the EU (London, UK) on secure cloud infrastructure. We do not transfer data outside the EEA without appropriate safeguards.</p>
                </section>

                <section>
                  <h2 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-3">8. Contact</h2>
                  <p>For data protection queries, contact your workspace Director or email <a href="mailto:privacy@furd-tech.com" className="text-[#1e3a5f] underline">privacy@furd-tech.com</a>.</p>
                </section>

                <section>
                  <h2 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-3">9. Updates</h2>
                  <p>We may update this policy periodically. Significant changes will be communicated via email to all users.</p>
                </section>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-slate-200 text-center">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} FURD Tech Ltd. All rights reserved.
            </p>
            <p className="text-slate-400 text-xs mt-2">
              Registered in England and Wales
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
